import { ArrowRight, Check, X, Zap, Target, GitBranch, Layers, TrendingUp, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 20, willChange: 'opacity, transform' as const },
  visible: (i: number = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: 'easeOut' },
  }),
};

const stagger = { visible: { transition: { staggerChildren: 0.06 } } };

type CellValue = boolean | string;

interface ComparisonRow {
  feature: string;
  values: CellValue[];
}

const competitors = ['IntentDrive', 'Boberdoo', 'LeadProsper', 'Ringba', 'Drift/Intercom'];

const rows: ComparisonRow[] = [
  { feature: 'Multi-vertical per session', values: [true, false, false, false, false] },
  { feature: 'Rejection pivoting', values: [true, false, false, false, false] },
  { feature: 'Revenue Per Session tracking', values: [true, false, false, false, false] },
  { feature: 'Real-time scoring engine', values: [true, false, false, 'Partial', false] },
  { feature: 'Call + Lead + Redirect routing', values: [true, false, false, 'Call only', 'Lead only'] },
  { feature: '100+ vertical catalog', values: [true, false, false, false, false] },
  { feature: 'Embeddable widget', values: [true, false, false, false, true] },
  { feature: 'Configurable scoring weights', values: [true, false, false, false, false] },
  { feature: 'Session replay', values: [true, false, false, false, false] },
  { feature: 'A/B testing', values: [true, false, false, false, false] },
  { feature: 'API access', values: [true, true, true, true, true] },
  { feature: 'Starting price', values: ['Free', '$500/mo', '$99/mo', 'Custom', 'Custom'] },
];

function CellContent({ value }: { value: CellValue }) {
  if (value === true) return <Check className="h-5 w-5 text-emerald-400 mx-auto" />;
  if (value === false) return <X className="h-5 w-5 text-slate-600 mx-auto" />;
  return <span className="text-sm text-slate-300">{value}</span>;
}

const differentiators = [
  {
    icon: <Layers className="h-6 w-6" />,
    title: 'Multi-Offer Revenue Loops',
    desc: 'Other tools route one lead to one buyer. IntentDrive stacks 3-5 verticals per session — auto, renter, loan, credit — so every click generates maximum revenue even when the first offer says no.',
    gradient: 'from-emerald-500 to-teal-500',
  },
  {
    icon: <GitBranch className="h-6 w-6" />,
    title: 'Rejection Pivoting',
    desc: 'When a buyer rejects a lead, competitors lose that user forever. IntentDrive instantly pivots to the next highest-paying vertical, turning a $0 session into a $15+ monetization event.',
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    icon: <TrendingUp className="h-6 w-6" />,
    title: 'Revenue Per Session (RPUS)',
    desc: 'Stop optimizing for clicks or CPL. RPUS is the only metric that captures total session value across all offers, pivots, and routes. IntentDrive tracks it natively — no one else does.',
    gradient: 'from-violet-500 to-purple-500',
  },
];

const Compare = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[15%] w-[800px] h-[800px] bg-emerald-500/[0.07] rounded-full blur-[150px]" />
        <div className="absolute bottom-[-10%] right-[5%] w-[600px] h-[600px] bg-blue-500/[0.06] rounded-full blur-[120px]" />
      </div>

      {/* Nav */}
      <header className="relative z-20 border-b border-slate-800/50 backdrop-blur-md bg-slate-950/80">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.jpg" alt="IntentDrive.ai" className="h-10 sm:h-14 w-auto rounded" style={{ filter: 'brightness(1.2) sepia(1) hue-rotate(100deg) saturate(2)' }} />
              <span className="font-bold text-lg sm:text-xl tracking-tight">IntentDrive</span>
              <span className="text-[10px] text-emerald-400 font-bold tracking-wider uppercase">.ai</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/compare">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">Compare</Button>
              </Link>
              <Link to="/portal/auth">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">Login</Button>
              </Link>
              <Link to="/portal/signup">
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20">
                  Start Free <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="relative z-10 container px-4 sm:px-6 mx-auto">

        {/* ═══ HERO ═══ */}
        <section className="pt-16 sm:pt-24 pb-16">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center max-w-3xl mx-auto">
            <motion.div variants={fadeUp} custom={0} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium mb-6">
              <Zap className="h-3 w-3" /> Head-to-Head Comparison
            </motion.div>
            <motion.h1 variants={fadeUp} custom={1} className="text-3xl sm:text-5xl md:text-6xl font-bold leading-[1.1] tracking-tight mb-6">
              IntentDrive vs <span className="text-emerald-400">The Rest</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
              See why performance marketers are switching to the only revenue routing engine built for multi-offer monetization.
            </motion.p>
          </motion.div>
        </section>

        {/* ═══ COMPARISON TABLE ═══ */}
        <section className="pb-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.05 }}
            variants={stagger}
          >
            <motion.div variants={fadeUp} className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/50 backdrop-blur-sm">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left px-5 py-4 text-sm font-medium text-slate-400 w-56">Feature</th>
                    {competitors.map((name, i) => (
                      <th key={name} className={`px-4 py-4 text-center text-sm font-semibold ${i === 0 ? 'text-emerald-400 bg-emerald-500/5' : 'text-slate-300'}`}>
                        {i === 0 && <Target className="h-4 w-4 mx-auto mb-1 text-emerald-400" />}
                        {name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={row.feature} className={`border-b border-slate-800/50 ${ri % 2 === 0 ? 'bg-slate-900/30' : ''}`}>
                      <td className="px-5 py-3.5 text-sm text-slate-300 font-medium">{row.feature}</td>
                      {row.values.map((val, vi) => (
                        <td key={vi} className={`px-4 py-3.5 text-center ${vi === 0 ? 'bg-emerald-500/5' : ''}`}>
                          <CellContent value={val} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          </motion.div>
        </section>

        {/* ═══ WHY INTENTDRIVE ═══ */}
        <section className="pb-24 border-t border-slate-800/50 pt-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.05 }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-slate-400 text-xs font-medium mb-4">Why IntentDrive?</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Built Different. <span className="text-emerald-400">Built for Revenue.</span>
              </h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">Three core advantages no competitor can match.</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {differentiators.map((d, i) => (
                <motion.div
                  key={d.title}
                  variants={fadeUp}
                  custom={i}
                  className="p-6 rounded-xl bg-gradient-to-b from-slate-900/50 to-slate-900/20 border border-slate-800 hover:border-slate-600 transition-all duration-300 hover:translate-y-[-2px]"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${d.gradient} flex items-center justify-center text-white shadow-lg mb-4`}>
                    {d.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{d.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{d.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ═══ CTA ═══ */}
        <section className="py-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.05 }}
            variants={stagger}
            className="relative rounded-2xl border border-emerald-500/20 bg-gradient-to-b from-emerald-500/5 to-transparent p-12 text-center overflow-hidden"
          >
            <div className="absolute inset-0 pointer-events-none" style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(52,211,153,0.03) 1px, transparent 0)`,
              backgroundSize: '30px 30px',
            }} />
            <motion.h2 variants={fadeUp} className="text-4xl font-bold mb-4 relative">
              Ready to <span className="text-emerald-400">Out-Earn the Competition</span>?
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-lg text-slate-400 mb-8 relative">
              Start building multi-offer flows in minutes. No credit card required.
            </motion.p>
            <motion.div variants={fadeUp} custom={2} className="relative">
              <Link to="/portal/signup">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-6 text-lg shadow-xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all hover:scale-[1.03]">
                  Start Free <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-800/50 py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.jpg" alt="IntentDrive.ai" className="h-12 w-auto rounded" style={{ filter: 'brightness(1.2) sepia(1) hue-rotate(100deg) saturate(2)' }} />
              <span className="font-bold">IntentDrive</span>
              <span className="text-xs text-emerald-400">.ai</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <Link to="/compare" className="hover:text-white transition-colors">Compare</Link>
              <Link to="/portal/auth" className="hover:text-white transition-colors">Login</Link>
              <Link to="/portal/signup" className="hover:text-white transition-colors">Sign Up</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            </div>
            <div className="text-sm text-slate-600">&copy; 2026 IntentDrive.ai</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Compare;
